/**
 * Asset Service
 * Integrates with /api/v1/Asset endpoints per ASSET_API_DOC_UI_INTEGRATION.md
 * API structure: assetCode auto-generated; serialNumberOrTag (single field) for Tool/Equipment; registrationNumber for Vehicle
 */
import axios from "axios";
import config from "../config";
import { APIClient, getLoggedinUser } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";

const api = new APIClient();
const apiConfig = config.api;

const getAuthToken = () => {
  const authUser = getLoggedinUser();
  return authUser?.token || "";
};

export interface AssetItem {
  id: string;
  tenantId: string;
  assetCode: string; // Read-only, auto-generated (VH-0001, TL-0001, EQ-0001)
  assetName?: string;
  displayName?: string; // Formatted: "AssetName (RegistrationNumber)" for Vehicle, "AssetName (SerialNumberOrTag)" for Tool/Equipment
  assetClass?: number;
  assetClassName?: string;
  status?: number;
  statusName?: string;
  aboutAsset?: string;
  serialNumberOrTag?: string;
  iotId?: string;
  registrationNumber?: string;
  engineNumber?: string;
  vin?: string;
  vehicleType?: number;
  vehicleTypeName?: string;
  make?: string;
  model?: string;
  colour?: string;
  yearOfManufacture?: number;
  odometer?: number;
  iotProviderCode?: number; // Read-only; set by backend sync
  registrationExpiryDate?: string;
  insuranceExpiryDate?: string;
  expiryDate?: string;
  isSafetyGear?: boolean;
  assetImage?: string;
  notes?: string;
}

export interface AssetCreatePayload {
  tenantId: string;
  assetName?: string;
  assetClass?: number;
  status?: number;
  aboutAsset?: string;
  serialNumberOrTag?: string;
  iotId?: string;
  registrationNumber?: string;
  engineNumber?: string;
  vin?: string;
  vehicleType?: number;
  make?: string;
  model?: string;
  colour?: string;
  yearOfManufacture?: number;
  odometer?: number;
  registrationExpiryDate?: string;
  insuranceExpiryDate?: string;
  expiryDate?: string;
  isSafetyGear?: boolean;
  notes?: string;
  assetImage?: File;
}

export interface AssetUpdatePayload extends Omit<AssetCreatePayload, "tenantId"> {}

export interface AssetApiResponse {
  id: string;
  isSuccess: boolean;
  notFound: boolean;
  message: string;
  modelErrors: any[];
  result: string;
}

/** Build FormData for create/update - do NOT send assetCode or iotProviderCode */
function buildAssetFormData(payload: AssetCreatePayload | AssetUpdatePayload, isCreate: boolean): FormData {
  const formData = new FormData();
  if (isCreate && "tenantId" in payload && payload.tenantId) {
    formData.append("tenantId", payload.tenantId);
  }
  if (payload.assetName) formData.append("assetName", payload.assetName);
  if (payload.assetClass != null) formData.append("assetClass", String(payload.assetClass));
  if (payload.status != null) formData.append("status", String(payload.status));
  if (payload.aboutAsset) formData.append("aboutAsset", payload.aboutAsset);
  if (payload.serialNumberOrTag) formData.append("serialNumberOrTag", payload.serialNumberOrTag);
  if (payload.iotId) formData.append("iotId", payload.iotId);
  if (payload.registrationNumber) formData.append("registrationNumber", payload.registrationNumber);
  if (payload.engineNumber) formData.append("engineNumber", payload.engineNumber);
  if (payload.vin) formData.append("vin", payload.vin);
  if (payload.vehicleType != null) formData.append("vehicleType", String(payload.vehicleType));
  if (payload.make) formData.append("make", payload.make);
  if (payload.model) formData.append("model", payload.model);
  if (payload.colour) formData.append("colour", payload.colour);
  if (payload.yearOfManufacture != null) formData.append("yearOfManufacture", String(payload.yearOfManufacture));
  if (payload.odometer != null) formData.append("odometer", String(payload.odometer));
  if (payload.registrationExpiryDate) formData.append("registrationExpiryDate", payload.registrationExpiryDate);
  if (payload.insuranceExpiryDate) formData.append("insuranceExpiryDate", payload.insuranceExpiryDate);
  if (payload.expiryDate) formData.append("expiryDate", payload.expiryDate);
  if (payload.isSafetyGear != null) formData.append("isSafetyGear", String(payload.isSafetyGear));
  if (payload.notes) formData.append("notes", payload.notes);
  if (payload.assetImage) formData.append("assetImage", payload.assetImage);
  return formData;
}

/**
 * Get all assets for tenant with optional filters
 * GET /api/v1/Asset/tenant/{tenantId}?assetClass={1|2|3}&status={1|2|3|4}
 */
export const getAssetsByTenant = async (
  tenantId: string,
  filters?: { assetClass?: number; status?: number }
): Promise<AssetItem[]> => {
  const params: Record<string, number> = {};
  if (filters?.assetClass) params.assetClass = filters.assetClass;
  if (filters?.status) params.status = filters.status;

  const queryString =
    Object.keys(params).length > 0
      ? "?" + new URLSearchParams(params as any).toString()
      : "";
  const response = await api.get<AssetItem[] | { items?: AssetItem[] }>(
    `${url.GET_ASSETS}/${tenantId}${queryString}`
  );

  if (Array.isArray(response)) {
    return response;
  }
  return (response as { items?: AssetItem[] })?.items || [];
};

/**
 * Get available assets for tenant (status=Available only)
 * GET /api/v1/Asset/tenant/{tenantId}/available?assetClass={1|2|3}
 * Use case: Dropdown for asset assignment, work order asset selection, planner asset pool
 */
export const getAvailableAssetsByTenant = async (
  tenantId: string,
  assetClass?: number,
  signal?: AbortSignal
): Promise<AssetItem[]> => {
  const queryString = assetClass ? `?assetClass=${assetClass}` : "";
  const response = await api.get<AssetItem[] | { items?: AssetItem[] }>(
    `${url.GET_ASSETS}/${tenantId}/available${queryString}`,
    undefined,
    signal ? { signal } : undefined
  );

  if (Array.isArray(response)) {
    return response;
  }
  return (response as { items?: AssetItem[] })?.items || [];
};

/**
 * Get single asset by ID
 * GET /api/v1/Asset/{id}
 */
export const getAssetById = async (id: string): Promise<AssetItem | null> => {
  try {
    const response = await api.get<AssetItem>(`${url.GET_ASSET_BY_ID}/${id}`);
    return response;
  } catch {
    return null;
  }
};

/**
 * Create asset (multipart/form-data)
 * POST /api/v1/Asset
 * assetCode is auto-generated; do NOT send assetCode or iotProviderCode
 */
export const createAsset = async (payload: AssetCreatePayload): Promise<AssetApiResponse> => {
  const token = getAuthToken();
  const formData = buildAssetFormData(payload, true);

  const response = await axios.post<AssetApiResponse>(
    `${apiConfig.API_URL}${url.CREATE_ASSET}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000,
    }
  );

  return response.data || response;
};

/**
 * Update asset (multipart/form-data)
 * PUT /api/v1/Asset/{id}
 * assetCode and iotProviderCode are read-only; do NOT send
 */
export const updateAsset = async (
  id: string,
  payload: AssetUpdatePayload
): Promise<AssetApiResponse> => {
  const token = getAuthToken();
  const formData = buildAssetFormData(payload, false);

  const response = await axios.put<AssetApiResponse>(
    `${apiConfig.API_URL}${url.UPDATE_ASSET}/${id}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000,
    }
  );

  return response.data || response;
};

/**
 * Delete asset
 * DELETE /api/v1/Asset/{id}
 */
export const deleteAsset = async (id: string): Promise<void> => {
  await api.delete<void>(`${url.DELETE_ASSET}/${id}`);
};
